<!-- eslint-disable perfectionist/sort-modules -->
<script setup lang="ts">
import { ref } from 'vue';

import type { CampaignFormErrors, SegmentationData } from '../../types';

import { ELIGIBLE_BRAND_OPTIONS } from '../../constants';

const model = defineModel<SegmentationData>({ required: true });

defineProps<{
  errors: CampaignFormErrors;
}>();

const partnerInput = ref('');
const partnerInputRef = ref<HTMLInputElement | null>(null);

function addPartnerIds(ids: string[]) {
  const existing = new Set(model.value.partnerIds);
  const newIds = ids.filter((id) => !existing.has(id));

  if (newIds.length === 0) return;

  model.value.partnerIds = [...model.value.partnerIds, ...newIds];
}

function parsePartnerIds(raw: string): string[] {
  return raw
    .split(/[\n,;\s]+/)
    .map((value) => value.trim())
    .filter(Boolean);
}

function commitCurrentToken() {
  const token = partnerInput.value.trim();
  if (!token) return;

  addPartnerIds([token]);
  partnerInput.value = '';
}

function commitInput() {
  const ids = parsePartnerIds(partnerInput.value);
  if (ids.length === 0) return;

  addPartnerIds(ids);
  partnerInput.value = '';
}

function focusPartnerInput() {
  partnerInputRef.value?.focus();
}

function getBrandLabel(value: string): string {
  return ELIGIBLE_BRAND_OPTIONS.find((brand) => brand.value === value)?.label ?? value;
}

function isBrandSelected(value: string): boolean {
  return model.value.brands.includes(value);
}

function onPartnerKeydown(event: KeyboardEvent) {
  if (event.key === 'Backspace' && !partnerInput.value && model.value.partnerIds.length > 0) {
    model.value.partnerIds.pop();
    return;
  }

  if (event.key === 'Enter') {
    event.preventDefault();
    commitInput();
    return;
  }

  if (event.key === ' ' || event.key === ',') {
    event.preventDefault();
    commitCurrentToken();
  }
}

function onPartnerPaste(event: ClipboardEvent) {
  event.preventDefault();

  const pasted = event.clipboardData?.getData('text') ?? '';
  const pending = partnerInput.value.trim();
  const combined = pending ? `${pending}\n${pasted}` : pasted;
  const ids = parsePartnerIds(combined);

  addPartnerIds(ids);
  partnerInput.value = '';
}

function removePartner(id: string) {
  model.value.partnerIds = model.value.partnerIds.filter((partnerId) => partnerId !== id);
}

function toggleBrand(value: string) {
  if (isBrandSelected(value)) {
    model.value.brands = model.value.brands.filter((brand) => brand !== value);
    return;
  }

  model.value.brands = [...model.value.brands, value];
}

function update<K extends keyof SegmentationData>(key: K, value: SegmentationData[K]) {
  model.value[key] = value;
}

function parseOptionalNumber(raw: string): null | number {
  const value = Number(raw);
  return raw.trim() === '' || Number.isNaN(value) ? null : value;
}

function updateCcbMin(raw: string) {
  const value = parseOptionalNumber(raw);
  model.value.ccbMin = value;

  if (value != null && model.value.ccbMaxPerClient != null && value > model.value.ccbMaxPerClient) {
    model.value.ccbMaxPerClient = value;
  }
}

function updateCcbMax(raw: string) {
  const value = parseOptionalNumber(raw);

  if (value == null) {
    model.value.ccbMaxPerClient = null;
    return;
  }

  const min = model.value.ccbMin ?? 1;
  model.value.ccbMaxPerClient = Math.max(value, min);
}
</script>

<template>
  <div class="fs-section">
    <div class="fs-header">
      <div class="fs-num">2</div>
      <h4 class="fs-title">Segmentação de parceiros</h4>
    </div>
    <p class="fs-sub">Quem é elegível para entrar nesta jornada?</p>

    <div class="row g-3">
      <div class="col-12">
        <label class="fs-label" for="partnerInput">
          Parceiros incluídos (lista de IDs) <span class="fs-req">*</span>
        </label>
        <div
          class="chips-box"
          :class="{ 'chips-box-error': errors.partners }"
          @click="focusPartnerInput"
          @keydown.enter="focusPartnerInput"
        >
          <span
            v-for="partnerId in model.partnerIds"
            :key="partnerId"
            class="chip"
          >
            {{ partnerId }}
            <button
              type="button"
              class="chip-remove"
              :aria-label="`Remover parceiro ${partnerId}`"
              @mousedown.prevent
              @click.stop="removePartner(partnerId)"
            >×</button>
          </span>
          <input
            id="partnerInput"
            ref="partnerInputRef"
            v-model="partnerInput"
            class="chip-input"
            placeholder="Digite IDs e pressione Enter, espaço ou vírgula…"
            @keydown="onPartnerKeydown"
            @paste="onPartnerPaste"
            @blur="commitInput"
          />
        </div>
        <div v-if="errors.partners" class="fs-error">{{ errors.partners }}</div>
        <small v-else class="fs-hint">
          Digite um ID e pressione Enter, espaço ou vírgula para fixar.
          Cole listas separadas por vírgula, espaço ou quebra de linha.
          <strong>Cada parceiro pode estar em apenas 1 campanha ativa por vez.</strong>
        </small>
      </div>

      <div class="col-12">
        <label class="fs-label">
          Marcas elegíveis <span class="fs-req">*</span>
        </label>
        <div
          class="chips-box"
          :class="{ 'chips-box-error': errors.brands }"
        >
          <span
            v-if="model.brands.length === 0"
            class="chip-hint-inline"
          >Nenhuma marca selecionada</span>
          <span
            v-for="brandValue in model.brands"
            :key="brandValue"
            class="chip"
          >
            {{ getBrandLabel(brandValue) }}
            <button
              type="button"
              class="chip-remove"
              :aria-label="`Remover marca ${getBrandLabel(brandValue)}`"
              @click="toggleBrand(brandValue)"
            >×</button>
          </span>
        </div>
        <div v-if="errors.brands" class="fs-error">{{ errors.brands }}</div>
        <div class="brand-toggles">
          <button
            v-for="brand in ELIGIBLE_BRAND_OPTIONS"
            :key="brand.value"
            type="button"
            class="brand-btn"
            :class="{ 'brand-btn-selected': isBrandSelected(brand.value) }"
            @click="toggleBrand(brand.value)"
          >{{ brand.label }}</button>
        </div>
        <small v-if="!errors.brands" class="fs-hint">
          Selecione as marcas de inversor elegíveis para esta campanha.
        </small>
      </div>

      <div class="col-12 col-md-6">
        <label class="fs-label">
          Faixa de atraso elegível <span class="fs-req">*</span>
        </label>
        <div class="row g-2">
          <div class="col-6">
            <label class="fs-hint" for="delayMin">De (dias)</label>
            <input
              id="delayMin"
              :value="model.delayMin"
              type="number"
              class="form-control"
              :class="{ 'is-invalid': errors.delayMin || errors.delayMax }"
              min="0"
              placeholder="10"
              @input="update('delayMin', parseOptionalNumber(($event.target as HTMLInputElement).value))"
            />
            <div v-if="errors.delayMin" class="fs-error">{{ errors.delayMin }}</div>
          </div>
          <div class="col-6">
            <label class="fs-hint" for="delayMax">Até (dias)</label>
            <input
              id="delayMax"
              :value="model.delayMax"
              type="number"
              class="form-control"
              :class="{ 'is-invalid': errors.delayMax }"
              min="0"
              placeholder="30"
              @input="update('delayMax', parseOptionalNumber(($event.target as HTMLInputElement).value))"
            />
          </div>
        </div>
        <div v-if="errors.delayMax" class="fs-error">{{ errors.delayMax }}</div>
        <small v-else-if="!errors.delayMin" class="fs-hint">
          Filtra CCBs pelo número de dias em atraso na instalação.
        </small>
      </div>

      <div class="col-12 col-md-6">
        <label class="fs-label">
          CCBs por parceiro <span class="fs-req">*</span>
        </label>
        <div class="row g-2">
          <div class="col-6">
            <label class="fs-hint" for="ccbMin">Mínimo</label>
            <input
              id="ccbMin"
              :value="model.ccbMin"
              type="number"
              class="form-control"
              :class="{ 'is-invalid': errors.ccbMin }"
              min="1"
              :max="model.ccbMaxPerClient ?? undefined"
              placeholder="2"
              @input="updateCcbMin(($event.target as HTMLInputElement).value)"
            />
            <div v-if="errors.ccbMin" class="fs-error">{{ errors.ccbMin }}</div>
          </div>
          <div class="col-6">
            <label class="fs-hint" for="ccbMaxPerClient">Máximo</label>
            <input
              id="ccbMaxPerClient"
              :value="model.ccbMaxPerClient"
              type="number"
              class="form-control"
              :class="{ 'is-invalid': errors.ccbMaxPerClient }"
              :min="model.ccbMin ?? 1"
              placeholder="3"
              @input="updateCcbMax(($event.target as HTMLInputElement).value)"
            />
            <div v-if="errors.ccbMaxPerClient" class="fs-error">{{ errors.ccbMaxPerClient }}</div>
          </div>
        </div>
        <small v-if="!errors.ccbMin && !errors.ccbMaxPerClient" class="fs-hint">
          Mínimo e máximo de CCBs por parceiro. O máximo é ajustado automaticamente para não ficar abaixo do mínimo.
        </small>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.fs-section {
  padding: 24px;
  border-bottom: 1px solid #e4e6eb;
}

.fs-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 6px;
}

.fs-num {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #00b569;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 12px;
  flex-shrink: 0;
}

.fs-title {
  font-size: 15px;
  font-weight: 700;
  color: #222222;
  margin: 0;
}

.fs-sub {
  font-size: 12.5px;
  color: #6b7280;
  margin-left: 36px;
  margin-bottom: 16px;
  line-height: 1.4;
}

.fs-label {
  font-size: 12px;
  font-weight: 600;
  color: #222222;
  margin-bottom: 4px;
  display: block;
}

.fs-req {
  color: #ef4444;
}

.fs-label-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
}

.fs-soon-badge {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 999px;
  background: #f3f4f6;
  border: 1px solid #e4e6eb;
  color: #6b7280;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.3px;
  text-transform: uppercase;
}

.fs-field-group--disabled {
  opacity: 0.65;
  pointer-events: none;
  user-select: none;
}

.fs-hint {
  font-size: 11.5px;
  color: #6b7280;
  line-height: 1.4;
  display: block;
  margin-top: 4px;
}

.fs-error {
  font-size: 11.5px;
  color: #ef4444;
  margin-top: 4px;
}

.form-control {
  padding: 9px 12px;
  border-color: #e4e6eb;
  border-radius: 6px;
  font-size: 13px;

  &:focus {
    border-color: #00b569;
    box-shadow: 0 0 0 3px rgba(0, 181, 105, 0.1);
  }
}

.chips-box {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding: 6px;
  border: 1px solid #e4e6eb;
  border-radius: 6px;
  min-height: 40px;
  background: #fff;
  cursor: text;

  &.chips-box-error {
    border-color: #ef4444;
  }
}

.chip {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 8px;
  background: #f3f4f6;
  color: #374151;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
  border: 1px solid #e5e7eb;
}

.chip-remove {
  cursor: pointer;
  font-weight: 700;
  font-size: 13px;
  line-height: 1;
  opacity: 0.7;
  border: none;
  background: none;
  padding: 0;
  color: inherit;

  &:hover {
    opacity: 1;
  }
}

.chip-input {
  flex: 1;
  min-width: 180px;
  border: none;
  outline: none;
  padding: 4px 6px;
  font-size: 13px;
  font-family: inherit;
  background: transparent;
  color: #222222;

  &::placeholder {
    color: #9ca3af;
  }
}

.chip-hint-inline {
  font-size: 12px;
  color: #9ca3af;
  padding: 4px 6px;
  align-self: center;
}

.brand-toggles {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 8px;
}

.brand-btn {
  padding: 3px 12px;
  border: 1px solid #e4e6eb;
  border-radius: 12px;
  font-size: 11.5px;
  font-weight: 600;
  color: #6b7280;
  background: #fff;
  cursor: pointer;
  transition: all 0.15s;

  &:hover {
    border-color: #00b569;
    color: #047857;
  }

  &.brand-btn-selected {
    background: #e6f8f0;
    border-color: #00b569;
    color: #047857;
  }
}

</style>
